'use client';

import React, { useState } from 'react';
import ExploreFurther from './ExploreFurther';

// ─── AR-RAHMAN / COMPASSION SECTION ──────────────────────────────────────────
// PDF Pages 18-20 — omitted from site between Islah and Al-Khabir/Al-Zahir

export function ArRahmanCompassionSection() {
  const [expanded, setExpanded] = useState(false);

  return (
    <section
      id="section-ar-rahman-compassion"
      style={{
        background: 'rgba(6,13,18,0.97)',
        borderTop: '1px solid rgba(201,168,76,0.08)',
        padding: '80px 0',
      }}
    >
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="mb-10">
          <p
            className="font-mono text-[9px] tracking-widest uppercase mb-4"
            style={{ color: 'rgba(139,105,20,0.6)' }}
          >
            ◈ Plot 04 · Section 08
          </p>
          <div className="flex items-start gap-6 mb-6">
            <div>
              <h2
                className="font-mono text-4xl md:text-5xl font-black uppercase tracking-tight mb-2"
                style={{ color: '#F5F0E8' }}
              >
                Ar-Rahman
              </h2>
              <p
                className="font-mono text-sm font-black uppercase tracking-widest mb-2"
                style={{ color: 'rgba(201,168,76,0.6)' }}
              >
                The Most Compassionate
              </p>
              <p
                style={{
                  fontFamily: "'Amiri', serif",
                  fontSize: 28,
                  color: 'rgba(201,168,76,0.7)',
                  direction: 'rtl',
                }}
              >
                الرَّحْمَٰن
              </p>
            </div>
          </div>

          {/* Root Analysis */}
          <div
            className="rounded-xl p-6 mb-8"
            style={{
              background: 'rgba(139,105,20,0.06)',
              border: '1px solid rgba(139,105,20,0.2)',
            }}
          >
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(139,105,20,0.6)' }}
            >
              Root · Ra - Ha - Mim
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                'Compassion',
                'Mercy',
                'Tenderness',
                'To have mercy',
                'To be compassionate',
                'To be tender',
                'Womb — Rahm',
                'The most expansive mercy',
                'Mercy that encompasses all of creation',
                'The mercy that precedes all else',
              ].map((m) => (
                <span
                  key={m}
                  className="font-mono text-[10px] px-2 py-1 rounded"
                  style={{
                    background: 'rgba(139,105,20,0.1)',
                    color: 'rgba(212,197,169,0.7)',
                    border: '1px solid rgba(139,105,20,0.15)',
                  }}
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Body Text — Compassion as the frame */}
        <div
          className="space-y-6 mb-10"
          style={{ color: 'rgba(212,197,169,0.75)', fontSize: 15, lineHeight: 1.9 }}
        >
          <p>
            <span style={{ color: '#c9a84c' }}>Ar-Rahman</span>; The Most Compassionate — the
            Name that opens the Quran, the Name that opens every Surah but one, the Name that
            precedes all action. The Bismillah that begins every act of the believer is a
            declaration: this is done in the Name of Allah, the Most Compassionate, the Most
            Especially Compassionate. Compassion is not a quality Allah has — it is a Name He
            holds. The distinction is total. A quality can be partial, conditional, temporary.
            A Name is an attribute of the highest order — the complete, unrestricted, eternal
            embodiment of the concept.
          </p>
          <p>
            The <span style={{ color: '#c9a84c', fontStyle: 'italic' }}>Rahma</span>, Compassion
            of Allah, is the frame within which all of creation exists. The Pen wrote the Qadr
            within the Rahma. The caretaker's role was decreed within the Rahma. The Mizan —
            the balance — is an expression of the Rahma. When the letter speaks of{' '}
            <span style={{ color: '#c9a84c', fontStyle: 'italic' }}>Islah</span> — to cultivate,
            mend, restore — it is the Rahma that motivates the mending. The bootstrapped
            operating system is being patched not out of frustration but out of compassion for
            those running it.
          </p>
          <p>
            The Quran describes the Messenger ﷺ as a mercy to all the worlds:{' '}
            <span style={{ color: '#c9a84c', fontStyle: 'italic' }}>
              "And We have not sent you except as a mercy to the worlds."
            </span>{' '}
            (Quran 21:107). The platform is an attempt to extend that mercy into the tools of
            the present moment — to make the recognition of Allah's Names accessible to
            caretakers in every ward, every vocation, every state of being.
          </p>
        </div>

        {/* Quran 1:1-7 — Al-Fatiha */}
        <div
          className="rounded-xl p-6 mb-6"
          style={{
            background: 'rgba(45,122,71,0.06)',
            border: '1px solid rgba(45,122,71,0.2)',
            borderLeft: '3px solid rgba(45,122,71,0.5)',
          }}
        >
          <p
            className="font-mono text-[9px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(45,122,71,0.7)' }}
          >
            Surah Al-Fatiha — The Opening · Quran 1:1-7
          </p>
          <p
            style={{
              color: 'rgba(212,197,169,0.8)',
              fontSize: 14,
              lineHeight: 1.85,
              fontStyle: 'italic',
            }}
          >
            "In the name of Allah, the Most Compassionate, the Most Especially Compassionate.
            All praise is for Allah — Lord of all worlds, the Most Compassionate, the Most
            Especially Compassionate, Master of the Day of Judgment. You alone we worship and
            You alone we ask for help. Guide us along the Straight Path — the Path of those You
            have blessed, not those You are displeased with, or those who are astray."
          </p>
        </div>

        {/* Quran 21:107 */}
        <div
          className="rounded-xl p-6 mb-6"
          style={{
            background: 'rgba(45,122,71,0.06)',
            border: '1px solid rgba(45,122,71,0.2)',
            borderLeft: '3px solid rgba(45,122,71,0.5)',
          }}
        >
          <p
            className="font-mono text-[9px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(45,122,71,0.7)' }}
          >
            Quran 21:107
          </p>
          <p
            style={{
              color: 'rgba(212,197,169,0.8)',
              fontSize: 14,
              lineHeight: 1.85,
              fontStyle: 'italic',
            }}
          >
            "And We have not sent you except as a mercy to the worlds."
          </p>
        </div>

        {/* Quran 7:156 */}
        <div
          className="rounded-xl p-6 mb-8"
          style={{
            background: 'rgba(45,122,71,0.06)',
            border: '1px solid rgba(45,122,71,0.2)',
            borderLeft: '3px solid rgba(45,122,71,0.5)',
          }}
        >
          <p
            className="font-mono text-[9px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(45,122,71,0.7)' }}
          >
            Quran 7:156
          </p>
          <p
            style={{
              color: 'rgba(212,197,169,0.8)',
              fontSize: 14,
              lineHeight: 1.85,
              fontStyle: 'italic',
            }}
          >
            "And ordain for us in this world good, and in the Hereafter. Indeed, we have turned
            back to You." He said, "My punishment — I afflict with it whom I will, but My mercy
            encompasses all things. So I will ordain it for those who fear Me and give Zakah and
            those who believe in Our verses."
          </p>
        </div>

        {/* Ar-Rahman / Ar-Rahim distinction */}
        <div
          className="rounded-xl p-6 mb-8"
          style={{
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.15)',
          }}
        >
          <p
            className="font-mono text-[9px] uppercase tracking-widest mb-4"
            style={{ color: 'rgba(201,168,76,0.5)' }}
          >
            Ar-Rahman · Ar-Rahim — The Paired Names of Mercy
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                name: 'Ar-Rahman',
                arabic: 'الرَّحْمَٰن',
                meaning:
                  'The Most Compassionate — the vast, all-encompassing mercy that extends to all of creation, believer and disbeliever, human and animal, seen and unseen. The mercy that is active in every breath, every provision, every moment of existence.',
                color: '#c9a84c',
              },
              {
                name: 'Ar-Rahim',
                arabic: 'الرَّحِيم',
                meaning:
                  'The Most Especially Compassionate — the specific, particular mercy reserved for the believers in the Hereafter. The mercy that responds to turning, to seeking, to the act of return. The mercy that the caretaker calls upon when they begin again.',
                color: '#2d7a47',
              },
            ].map((r) => (
              <div
                key={r.name}
                className="rounded-lg p-4"
                style={{
                  background: `${r.color}08`,
                  border: `1px solid ${r.color}20`,
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="font-mono text-sm font-black uppercase"
                    style={{ color: r.color }}
                  >
                    {r.name}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Amiri', serif",
                      fontSize: 18,
                      color: `${r.color}70`,
                      direction: 'rtl',
                    }}
                  >
                    {r.arabic}
                  </span>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7 }}>
                  {r.meaning}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Explore Further: Platform Ground · Ar-Rahman as Foundation ── */}
        <ExploreFurther label="Explore Further — Platform Ground · Ar-Rahman" accentColor="rgba(45,122,71,0.6)">
          <div
            className="rounded-xl p-8"
            style={{
              background: 'rgba(45,122,71,0.06)',
              border: '1px solid rgba(45,122,71,0.25)',
              borderLeft: '4px solid rgba(45,122,71,0.5)',
            }}
          >
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-4"
              style={{ color: 'rgba(45,122,71,0.7)' }}
            >
              Platform Ground · Ar-Rahman as Foundation
            </p>
            <p
              style={{
                color: 'rgba(212,197,169,0.8)',
                fontSize: 15,
                lineHeight: 1.9,
              }}
            >
              The platform begins with Bismillah — in the Name of Allah, Ar-Rahman, Ar-Rahim. Every
              module, every quest, every observation submitted is done within the frame of the Most
              Compassionate. The Islah — the mending — is motivated by Rahma. The caretaker who
              tends the garden does so because they recognize that the garden, the ward, the
              community, the self — all are held within the mercy of Ar-Rahman. The IRL Lens is a
              tool for seeing what the Most Compassionate has placed in creation for those who look.
            </p>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              {
                label: 'IRL Lens',
                desc: 'Every observation begins with Bismillah — the lens is opened in the Name of Ar-Rahman',
                href: '/inner-reality-lens',
                color: '#c9a84c',
              },
              {
                label: 'Names Reference',
                desc: 'Ar-Rahman — the Name that opens the Quran and every act of the believer',
                href: '/names-reference',
                color: '#8a8ab8',
              },
              {
                label: 'Muslim Resource Guide',
                desc: 'Compassion as the motivation for Islah — the MRG is an act of Rahma toward the community',
                href: '/muslim-resource-guide',
                color: '#2d7a47',
              },
              {
                label: 'Keys to Kulli Haal',
                desc: "Key 03: Amanah — the trust is held within the frame of Ar-Rahman's mercy",
                href: '/keys-to-kulli-haal',
                color: '#2d8a87',
              },
            ].map((b) => (
              <a
                key={b.label}
                href={b.href}
                className="block rounded-xl p-4 transition-all"
                style={{
                  background: `${b.color}06`,
                  border: `1px solid ${b.color}20`,
                  textDecoration: 'none',
                }}
              >
                <p
                  className="font-mono text-[10px] font-black uppercase tracking-widest mb-1"
                  style={{ color: b.color }}
                >
                  {b.label}
                </p>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.6 }}>
                  {b.desc}
                </p>
              </a>
            ))}
          </div>
        </ExploreFurther>
      </div>
    </section>
  );
}

// ─── HADITH OF JIBRIL SECTION ─────────────────────────────────────────────────
// PDF Pages 20-21 — omitted from site between Ar-Rahman and Al-Khabir/Al-Zahir

export function HadithJibrilSection() {
  const [activeTab, setActiveTab] = useState<'hadith' | 'islam' | 'iman' | 'ihsan'>('hadith');

  return (
    <section
      id="section-hadith-jibril"
      style={{
        background: 'rgba(8,16,24,0.98)',
        borderTop: '1px solid rgba(201,168,76,0.08)',
        padding: '80px 0',
      }}
    >
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="mb-10">
          <p
            className="font-mono text-[9px] tracking-widest uppercase mb-4"
            style={{ color: 'rgba(139,105,20,0.6)' }}
          >
            ◈ Plot 04 · Section 09
          </p>
          <div
            className="rounded-xl p-4 mb-6"
            style={{
              background: 'rgba(201,168,76,0.04)',
              border: '1px solid rgba(201,168,76,0.15)',
            }}
          >
            <p
              className="font-mono text-[9px] uppercase tracking-widest"
              style={{ color: 'rgba(201,168,76,0.5)' }}
            >
              Hadith of Jibril — The Three Pillars
            </p>
          </div>
          <h2
            className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4"
            style={{ color: '#F5F0E8' }}
          >
            Islam · Iman · Ihsan
          </h2>
          <p
            style={{
              fontFamily: "'Amiri', serif",
              fontSize: 22,
              color: 'rgba(201,168,76,0.7)',
              direction: 'rtl',
              marginBottom: 16,
            }}
          >
            إِسْلَام · إِيمَان · إِحْسَان
          </p>
        </div>

        {/* The Hadith — full text */}
        <div
          className="rounded-xl p-6 mb-8"
          style={{
            background: 'rgba(138,138,184,0.06)',
            border: '1px solid rgba(138,138,184,0.2)',
          }}
        >
          <p
            className="font-mono text-[9px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(138,138,184,0.6)' }}
          >
            On the authority of Umar ibn al-Khattab · Sahih Muslim 8a · Grade: Sahih (Sound)
          </p>
          <div
            className="space-y-4"
            style={{ color: 'rgba(212,197,169,0.8)', fontSize: 14, lineHeight: 1.9, fontStyle: 'italic' }}
          >
            <p>
              One day while we were sitting with the Messenger of Allah ﷺ there appeared before
              us a man whose clothes were exceedingly white and whose hair was exceedingly black;
              no signs of journeying were to be seen on him and none of us knew him. He walked up
              and sat down by the Prophet ﷺ. He rested his knees against his and placed the palms
              of his hands on his thighs and said: "O Muhammad, tell me about Islam."
            </p>
            <p>
              The Messenger of Allah ﷺ said: "Islam is to testify that there is no god but Allah
              and Muhammad is the Messenger of Allah, to perform the prayers, to pay the Zakat, to
              fast in Ramadan, and to make the pilgrimage to the House if you are able to do so." He said:"You have spoken rightly," and we were amazed at him asking him and saying
              that he had spoken rightly.
            </p>
            <p>
              He said: "Then tell me about Iman." He said: "It is to believe in Allah, His angels,
              His books, His messengers, and the Last Day, and to believe in divine destiny, both
              the good and the evil thereof." He said: "You have spoken rightly."
            </p>
            <p>
              He said: "Then tell me about Ihsan." He said: "It is to worship Allah as though you
              could see Him, for if you cannot see Him then truly He sees you."
            </p>
            <p>
              He said: "Then tell me about the Hour." He said: "The one questioned about it knows no more than the questioner." He said: "Then tell me about its signs." He said: "That
              the slave-girl will give birth to her mistress and that you will see the barefooted,
              naked, destitute herdsmen competing in constructing lofty buildings."
            </p>
            <p>
              Then he took himself off and I stayed for a time. Then he said: "O Umar, do you know
              who the questioner was?" I said: "Allah and His Messenger know best." He said: "He was Jibril (Gabriel), who came to you to teach you your religion."
            </p>
          </div>
        </div>

        {/* Tab selector */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'hadith', label: 'The Hadith' },
            { id: 'islam', label: 'Islam' },
            { id: 'iman', label: 'Iman' },
            { id: 'ihsan', label: 'Ihsan' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as typeof activeTab)}
              className="font-mono text-[9px] tracking-widest uppercase px-4 py-2 transition-all"
              style={{
                background:
                  activeTab === t.id ? 'rgba(201,168,76,0.12)' : 'transparent',
                border: `1px solid ${
                  activeTab === t.id
                    ? 'rgba(201,168,76,0.4)'
                    : 'rgba(255,255,255,0.08)'
                }`,
                color:
                  activeTab === t.id
                    ? '#c9a84c' :'rgba(212,197,169,0.4)',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'hadith' && (
          <div
            className="space-y-4"
            style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.85 }}
          >
            <p>
              The Hadith of Jibril is the most comprehensive single teaching in the Sunnah. In
              one encounter, the entire architecture of the Deen is laid out: the outer practice
              (Islam), the inner conviction (Iman), and the quality of presence (Ihsan). Jibril
              came in human form — unrecognized by the Companions — to teach the religion to the
              community through the Prophet ﷺ. The teaching was for everyone present, not just
              the Prophet.
            </p>
          </div>
        )}

        {activeTab === 'islam' && (
          <div
            className="rounded-xl p-6"
            style={{
              background: 'rgba(45,122,71,0.06)',
              border: '1px solid rgba(45,122,71,0.25)',
            }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <h3
                  className="font-mono text-2xl font-black uppercase tracking-tight mb-1"
                  style={{ color: '#F5F0E8' }}
                >
                  Islam
                </h3>
                <p
                  style={{
                    fontFamily: "'Amiri', serif",
                    fontSize: 22,
                    color: 'rgba(45,122,71,0.8)',
                    direction: 'rtl',
                  }}
                >
                  إِسْلَام
                </p>
              </div>
            </div>
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(45,122,71,0.6)' }}
            >
              Root · Sin - Lam - Mim · Submission, Peace, Connection
            </p>
            <div
              className="space-y-3"
              style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.85 }}
            >
              <p>
                The five pillars: Shahada (testimony), Salah (prayer), Zakat (purifying
                charity), Sawm (fasting), Hajj (pilgrimage). These are the outer structure —
                the Zahir of the Deen. The observable, verifiable, communal practices that
                constitute the caretaker's outer submission.
              </p>
              <p>
                In the platform: Islam corresponds to the Zahir layer of the IRL Lens — the
                observable encounter, the sensory data, the outer practice. The caretaker's
                Salah is the most direct expression of Islam; the Islah (mending) the letter
                describes shares the same root as Salah. The outer practice is the foundation
                upon which the inner conviction and quality of presence are built.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'iman' && (
          <div
            className="rounded-xl p-6"
            style={{
              background: 'rgba(138,138,184,0.06)',
              border: '1px solid rgba(138,138,184,0.25)',
            }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <h3
                  className="font-mono text-2xl font-black uppercase tracking-tight mb-1"
                  style={{ color: '#F5F0E8' }}
                >
                  Iman
                </h3>
                <p
                  style={{
                    fontFamily: "'Amiri', serif",
                    fontSize: 22,
                    color: 'rgba(138,138,184,0.8)',
                    direction: 'rtl',
                  }}
                >
                  إِيمَان
                </p>
              </div>
            </div>
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(138,138,184,0.6)' }}
            >
              Root · Alif - Mim - Nun · Faith, Conviction, Security, Trust
            </p>
            <div
              className="space-y-3"
              style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.85 }}
            >
              <p>
                The six pillars of Iman: belief in Allah, His angels, His books, His
                messengers, the Last Day, and divine destiny (Qadr) — both the good and the
                evil thereof. The last pillar connects directly to the Al-Qadir section: the
                Pen wrote the Qadr; Iman includes accepting that decree.
              </p>
              <p>
                In the platform: Iman corresponds to the Yaqeen levels — Ilm al-Yaqeen
                (knowledge-certainty), Ayn al-Yaqeen (witnessed-certainty), Haqq al-Yaqeen
                (true-certainty). The three levels of Yaqeen are the three levels of Iman made
                operational in the recognition pathway. The caretaker's Iman deepens as their
                Yaqeen level rises through the IRL Lens encounters.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'ihsan' && (
          <div
            className="rounded-xl p-6"
            style={{
              background: 'rgba(201,168,76,0.06)',
              border: '1px solid rgba(201,168,76,0.25)',
            }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <h3
                  className="font-mono text-2xl font-black uppercase tracking-tight mb-1"
                  style={{ color: '#F5F0E8' }}
                >
                  Ihsan
                </h3>
                <p
                  style={{
                    fontFamily: "'Amiri', serif",
                    fontSize: 22,
                    color: 'rgba(201,168,76,0.8)',
                    direction: 'rtl',
                  }}
                >
                  إِحْسَان
                </p>
              </div>
            </div>
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(201,168,76,0.6)' }}
            >
              Root · Ha - Sin - Nun · Excellence, Beauty, Goodness, To do well
            </p>
            <div
              className="space-y-3"
              style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.85 }}
            >
              <p>
                <span style={{ color: '#c9a84c', fontStyle: 'italic' }}>
                  "It is to worship Allah as though you could see Him, for if you cannot see
                  Him then truly He sees you."
                </span>{' '}
                Ihsan is the quality of presence — the awareness that every act is witnessed.
                It is the highest level of the Deen: not just doing the right thing, but doing
                it with the awareness of being seen by the One Who sees all.
              </p>
              <p>
                In the platform: Ihsan is the Niyyah Engine — the quality of intention that
                precedes every action. The caretaker who sets their Niyyah before opening the
                IRL Lens is practicing Ihsan: they are bringing the awareness of being seen
                into the act of seeing. The observation submitted with Ihsan is not just data
                — it is an act of worship. The garden tended with Ihsan is not just a tool —
                it is a sajda.
              </p>
              <p>
                The IRL Lens's deepest layer is Ihsan: the caretaker who reaches Haqq
                al-Yaqeen is worshipping as though they see Him. The lens becomes transparent.
                The observer and the observed collapse into the recognition of the One Who
                sees.
              </p>
            </div>
          </div>
        )}

        {/* Three Pillars Summary */}
        <div
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {[
            {
              pillar: 'Islam',
              arabic: 'إِسْلَام',
              platform: 'Submission, Peace, Connection',
              color: '#2d7a47',
              icon: '◎',
            },
            {
              pillar: 'Iman',
              arabic: 'إِيمَان',
              platform: 'Belief, Conviction, Certainty',
              color: '#8a8ab8',
              icon: '◈',
            },
            {
              pillar: 'Ihsan',
              arabic: 'إِحْسَان',
              platform: 'Excellence, Presence, As Though You See Him',
              color: '#c9a84c',
              icon: '✦',
            },
          ].map((p) => (
            <div
              key={p.pillar}
              className="rounded-xl p-5 text-center"
              style={{
                background: `${p.color}06`,
                border: `1px solid ${p.color}25`,
              }}
            >
              <p
                className="font-mono text-lg mb-1"
                style={{ color: p.color }}
              >
                {p.icon}
              </p>
              <h4
                className="font-mono text-sm font-black uppercase tracking-widest mb-1"
                style={{ color: '#F5F0E8' }}
              >
                {p.pillar}
              </h4>
              <p
                style={{
                  fontFamily: "'Amiri', serif",
                  fontSize: 16,
                  color: `${p.color}70`,
                  direction: 'rtl',
                  marginBottom: 8,
                }}
              >
                {p.arabic}
              </p>
              <p
                style={{
                  color: 'rgba(212,197,169,0.5)',
                  fontSize: 11,
                  lineHeight: 1.6,
                }}
              >
                {p.platform}
              </p>
            </div>
          ))}
        </div>

        {/* ── Explore Further: Platform Architecture + Jibril's Teaching Method ── */}
        <ExploreFurther label="Explore Further — Platform Architecture · Jibril's Teaching Method" accentColor="rgba(201,168,76,0.5)">
          <div
            className="rounded-xl p-6 mb-4"
            style={{
              background: 'rgba(201,168,76,0.04)',
              border: '1px solid rgba(201,168,76,0.12)',
            }}
          >
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(201,168,76,0.5)' }}
            >
              Jibril's Teaching Method — The Platform's Pedagogy
            </p>
            <p
              style={{
                color: 'rgba(212,197,169,0.65)',
                fontSize: 13,
                lineHeight: 1.75,
              }}
            >
              Jibril came in human form — unrecognized, sitting knee to knee with the Prophet ﷺ,
              asking questions the community needed answered. The teaching was experiential: not
              a lecture but a dialogue. Not a text but an encounter. The platform follows this
              pedagogy: the caretaker learns by encountering, by asking the Three Questions, by
              sitting with the signs in creation until the recognition comes. The Hadith of Jibril
              is the prototype of the guided walkthrough — a stranger arrives, asks the essential
              questions, receives the essential answers, and departs. The community is left with
              the teaching. Jibril was the first guided walkthrough.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              {
                pillar: 'Islam',
                platform: 'Zahir Layer · Outer Practice · Observable Actions',
                color: '#2d7a47',
              },
              {
                pillar: 'Iman',
                platform: 'Yaqeen Levels · Inner Conviction · Certainty Depth',
                color: '#8a8ab8',
              },
              {
                pillar: 'Ihsan',
                platform: 'Niyyah Engine · Quality of Presence · As Though You See Him',
                color: '#c9a84c',
              },
            ].map((p) => (
              <div
                key={p.pillar}
                className="rounded-lg p-4"
                style={{ background: `${p.color}06`, border: `1px solid ${p.color}20` }}
              >
                <p className="font-mono text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: p.color }}>{p.pillar}</p>
                <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6 }}>{p.platform}</p>
              </div>
            ))}
          </div>
        </ExploreFurther>
      </div>
    </section>
  );
}

// ─── COMBINED EXPORT ──────────────────────────────────────────────────────────

export default function Plot4ArRahmanJibril() {
  return (
    <>
      <ArRahmanCompassionSection />
      <HadithJibrilSection />
    </>
  );
}
